import axios from 'axios';

const api = axios.create({
  baseURL: '',
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('lz_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('lz_token');
      localStorage.removeItem('lz_username');
      localStorage.removeItem('lz_auth_method');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const getTenants = () => api.get('/tenant/list');
export const getTenant = (id) => api.get(`/tenant/${id}`);
export const deleteTenant = (id) => api.delete(`/tenant/${id}`);
export const syncTenant = (id) => api.post(`/tenant/${id}/sync`, {}, { timeout: 300000 });
export const getResources = (id) => api.get(`/tenant/${id}/resources`);
export const getDriftReport = (id) => api.get(`/drift/report/${id}`);
export const setBaseline = (id, data) => api.post(`/drift/baseline/${id}`, data);
export const getBaselines = (id) => api.get(`/drift/baselines/${id}`);
export const activateBaseline = (tenantId, baselineId) => api.post(`/drift/baseline/${tenantId}/activate/${baselineId}`);
export const deleteBaseline = (tenantId, baselineId) => api.delete(`/drift/baseline/${tenantId}/${baselineId}`);
export const getRuleTemplates = (provider) => api.get('/drift/templates', { params: { provider: provider || 'azure' } });
export const getBaselineRules = (baselineId) => api.get(`/drift/baseline/${baselineId}/rules`);
export const addBaselineRule = (baselineId, data) => api.post(`/drift/baseline/${baselineId}/rules`, data);
export const addRulesFromTemplate = (baselineId, templateIds) => api.post(`/drift/baseline/${baselineId}/rules/from-template`, templateIds);
export const updateRule = (ruleId, data) => api.put(`/drift/rules/${ruleId}`, data);
export const deleteRule = (ruleId) => api.delete(`/drift/rules/${ruleId}`);
export const toggleRule = (ruleId) => api.post(`/drift/rules/${ruleId}/toggle`);
export const getCloudPolicies = (tenantId) => api.get(`/drift/cloud-policies/${tenantId}`);
export const getCompliancePosture = (tenantId) => api.get(`/drift/compliance-posture/${tenantId}`);
export const getSprawlReport = (id) => api.get(`/sprawl/report/${id}`);
export const getComplexityScore = (id) => api.get(`/complexity/score/${id}`);
export const getMaturityScore = (id) => api.get(`/maturity/score/${id}`);
export const getGovernanceGaps = (id) => api.get(`/governance/gaps/${id}`);
export const getEnvironmentCompare = (id) => api.get(`/environment/compare/${id}`);
export const getRiskForecast = (id) => api.get(`/risk/forecast/${id}`);
export const onboardTenant = (data) => api.post('/tenant/onboard', data);
export const validateTenant = (data) => api.post('/tenant/validate', data);

export const getGovernanceFramework = (id) => api.get(`/governance-framework/assess/${id}`);
export const getGovernanceFrameworkHistory = (id) => api.get(`/governance-framework/history/${id}`);
export const getPillarRemediation = (tenantId, pillarKey) => api.get(`/governance-framework/remediation/${tenantId}/${pillarKey}`);
export const getAIRemediationGuide = (data) => api.post('/governance-framework/ai-remediation-guide', data);
export const getControlResources = (tenantId, standardName, controlName) => api.get(`/drift/control-resources/${tenantId}/${standardName}/${controlName}`);
export const getAIPolicyRemediation = (data) => api.post('/drift/ai-policy-remediation', data);

export const getTemplates = () => api.get('/templates/list');
export const getDefaultTemplate = () => api.get('/templates/default');
export const getTemplate = (id) => api.get(`/templates/${id}`);
export const createTemplate = (data) => api.post('/templates/create', data);
export const updateTemplate = (id, data) => api.put(`/templates/${id}`, data);
export const deleteTemplate = (id) => api.delete(`/templates/${id}`);
export const exportTemplateJson = (id) => api.get(`/templates/${id}/export/json`);
export const cloneTemplate = (id) => api.post(`/templates/${id}/clone`);

export const getPolicies = (tenantId) => api.get(`/policy/list${tenantId ? `?tenant_id=${tenantId}` : ''}`);
export const createPolicy = (data) => api.post('/policy/create', data);
export const updatePolicy = (id, data) => api.put(`/policy/${id}`, data);
export const deletePolicy = (id) => api.delete(`/policy/${id}`);
export const togglePolicy = (id) => api.post(`/policy/${id}/toggle`);
export const seedDefaultPolicies = (tenantId) => api.post(`/policy/seed-defaults${tenantId ? `?tenant_id=${tenantId}` : ''}`);
export const validatePolicies = (data) => api.post('/policy/validate', data);
export const getValidationHistory = (id) => api.get(`/policy/validation-history/${id}`);

export const getDevContext = (id) => api.get(`/dev-context/profile/${id}`);
export const exportCiCdConfig = (id, format) => api.get(`/dev-context/profile/${id}/export/cicd?format=${format}`);

export const getGitHubExport = (id) => api.get(`/github/export/${id}`);
export const getGitHubBundle = (id) => api.get(`/github/export/${id}/bundle`);
export const getGitHubRepoFiles = (id) => api.get(`/github/repo-files/${id}`);

export const getAIRecommendations = (id) => api.get(`/ai-ops/recommendations/${id}`);
export const getAIGovernanceRecommendation = (data) => api.post('/api/ai-governance-recommendation', data, { timeout: 120000 });
export const getAIDetailedGovernanceReport = (data) => api.post('/api/ai-detailed-governance-report', data, { timeout: 120000 });
export const downloadGovernancePDF = (data) => api.post('/api/generate-governance-pdf', data, { responseType: 'blob', timeout: 120000 });
export const getWorkshopAIInsights = (data) => api.post('/api/discovery-workshop-ai', data, { timeout: 120000 });
export const downloadWorkshopPDF = (data) => api.post('/api/generate-workshop-pdf', data, { responseType: 'blob', timeout: 120000 });
export const getAILandingZoneAnalysis = (data) => api.post('/api/ai-landing-zone-analysis', data, { timeout: 120000 });
export const getUnifiedWorkshopAI = (data) => api.post('/api/unified-workshop-ai', data, { timeout: 120000 });
export const getStageRecommendation = (data) => api.post('/api/stage-recommendation', data, { timeout: 120000 });
export const getAICapabilities = () => api.get('/ai-ops/capabilities');
export const getAIHistory = (id) => api.get(`/ai-ops/history/${id}`);

export default api;
